import { FC, useEffect, useState } from 'react';
import type { SopItem, SopLinha, SopMes } from '@/types/dashboards';
import { produzidoBase, type FiltroFaixa } from '@/utils/equiplexSop';
import { fmtBRL, fmtData, fmtDesvio, fmtPreco, fmtUn, mesCurto } from '@/utils/painelFormat';
import type { ModoTema } from '../chartTheme';
import FaixaBadge from '../FaixaBadge';
import PainelCard from '../PainelCard';

const PAGINA = 50;

interface Props {
  linhas: SopLinha[];
  itens: Record<string, SopItem>;
  meses: SopMes[];
  mesesSelecionados: string[];
  busca: string;
  onBusca: (v: string) => void;
  mesTabela: string;
  onMesTabela: (v: string) => void;
  faixa: FiltroFaixa;
  onFaixa: (v: FiltroFaixa) => void;
  modo: ModoTema;
  /** Último dia fechado: no mês em andamento só entram as OPs até aqui. */
  fechadoAte: string;
}

const OPCOES_FAIXA: { valor: FiltroFaixa; rotulo: string }[] = [
  { valor: 'todas', rotulo: 'Todas as faixas' },
  { valor: 'critico', rotulo: 'Crítico' },
  { valor: 'alerta', rotulo: 'Alerta' },
  { valor: 'normal', rotulo: 'No plano' },
  { valor: 'sem_programa', rotulo: 'Sem OP no mês' },
  { valor: 'futuro', rotulo: 'Futuro' },
];

const campo = 'bg-surface-primary border border-theme-border rounded-lg px-3 py-1.5 text-sm text-content-primary';

/** Tabela SKU×mês: a "visão em tabela" de tudo que os gráficos resumem. */
const DesviosTable: FC<Props> = ({
  linhas,
  itens,
  meses,
  mesesSelecionados,
  busca,
  onBusca,
  mesTabela,
  onMesTabela,
  faixa,
  onFaixa,
  modo,
  fechadoAte,
}) => {
  const [limite, setLimite] = useState(PAGINA);
  useEffect(() => setLimite(PAGINA), [busca, mesTabela, faixa, mesesSelecionados]);
  const porChave = Object.fromEntries(meses.map((m) => [m.key, m]));
  const visiveis = linhas.slice(0, limite);

  return (
    <PainelCard
      testId="sop-tabela-desvios"
      titulo="Desvios por SKU"
      subtitulo={`Programado e produzido pela data de início da OP. No mês em andamento, só as OPs até ${fmtData(fechadoAte)} (dias fechados).`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={busca}
          onChange={(e) => onBusca(e.target.value)}
          placeholder="Código (111.0716 ou 1110716) ou descrição"
          aria-label="Buscar SKU"
          className={`${campo} w-full sm:w-72`}
        />
        <select aria-label="Mês" value={mesTabela} onChange={(e) => onMesTabela(e.target.value)} className={campo}>
          <option value="todos">Meses selecionados</option>
          {meses
            .filter((m) => mesesSelecionados.includes(m.key))
            .map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
        </select>
        <select aria-label="Faixa" value={faixa} onChange={(e) => onFaixa(e.target.value as FiltroFaixa)} className={campo}>
          {OPCOES_FAIXA.map((o) => (
            <option key={o.valor} value={o.valor}>
              {o.rotulo}
            </option>
          ))}
        </select>
        <span className="text-xs text-content-muted" data-testid="sop-tabela-contagem">
          {fmtUn(linhas.length)} {linhas.length === 1 ? 'linha' : 'linhas'}
        </span>
      </div>

      <div className="overflow-x-auto -mx-5 px-5">
        <table className="w-full min-w-[1080px] text-sm">
          <thead>
            <tr className="border-b border-theme-border text-left text-2xs uppercase tracking-wide text-content-muted">
              <th className="py-2 pr-3 font-medium">SKU</th>
              <th className="py-2 pr-3 font-medium">Mês</th>
              <th className="py-2 pr-3 text-right font-medium">OPs</th>
              <th className="py-2 pr-3 text-right font-medium">Programado</th>
              <th className="py-2 pr-3 text-right font-medium">Produzido</th>
              <th className="py-2 pr-3 text-right font-medium">Desvio</th>
              <th className="py-2 pr-3 font-medium">Faixa</th>
              <th className="py-2 pr-3 text-right font-medium">Liberado CQ</th>
              <th className="py-2 pr-3 text-right font-medium" title="Lotes que entraram no CQ e ainda não foram liberados">
                Lotes no CQ
              </th>
              <th className="py-2 pr-3 text-right font-medium">Faturado</th>
              <th className="py-2 text-right font-medium">Preço médio</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {visiveis.map((l) => {
              const item = itens[l.codigo_datasul];
              const mes = porChave[l.mes];
              return (
                <tr key={`${l.codigo}-${l.mes}`} className="border-b border-theme-border hover:bg-surface-hover">
                  <td className="py-2 pr-3 max-w-[280px]">
                    <div className="font-medium text-content-primary">{l.codigo_datasul}</div>
                    <div className="truncate text-xs text-content-muted" title={item?.descricao}>
                      {item?.descricao ?? 'Item sem cadastro'}
                    </div>
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap text-content-secondary">
                    {mes ? mesCurto(mes.label) : l.mes}
                    {l.em_andamento && <span className="ml-1 text-2xs text-content-muted">(parcial)</span>}
                  </td>
                  <td className="py-2 pr-3 text-right text-content-secondary">{l.ops ? fmtUn(l.ops) : '—'}</td>
                  <td
                    className="py-2 pr-3 text-right"
                    title={l.em_andamento ? `Programado no mês inteiro: ${fmtUn(l.programado)}` : undefined}
                  >
                    {l.futuro ? fmtUn(l.programado) : fmtUn(l.programado_base)}
                  </td>
                  <td
                    className="py-2 pr-3 text-right"
                    title={l.em_andamento ? `Produzido de todas as OPs do mês até agora: ${fmtUn(l.produzido)}` : undefined}
                  >
                    {fmtUn(produzidoBase(l))}
                  </td>
                  <td className="py-2 pr-3 text-right font-medium text-content-primary">{fmtDesvio(l.desvio_pct)}</td>
                  <td className="py-2 pr-3">
                    <FaixaBadge classe={l.classe} modo={modo} />
                  </td>
                  <td className="py-2 pr-3 text-right text-content-secondary">{fmtUn(l.liberado_cq)}</td>
                  <td className="py-2 pr-3 text-right text-content-secondary">
                    {l.lotes_pendentes_cq ? fmtUn(l.lotes_pendentes_cq) : '—'}
                  </td>
                  <td className="py-2 pr-3 text-right">{l.faturado_valor ? fmtBRL(l.faturado_valor) : '—'}</td>
                  <td className="py-2 text-right text-content-secondary">{fmtPreco(l.pmv_realizado)}</td>
                </tr>
              );
            })}
            {linhas.length === 0 && (
              <tr>
                <td colSpan={11} className="py-8 text-center text-content-muted">
                  Nenhum SKU com esses filtros.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {linhas.length > limite && (
        <button
          type="button"
          onClick={() => setLimite((v) => v + PAGINA)}
          className="self-center text-sm text-content-muted underline hover:text-content-primary"
        >
          Mostrar mais {fmtUn(Math.min(PAGINA, linhas.length - limite))} (faltam {fmtUn(linhas.length - limite)})
        </button>
      )}
    </PainelCard>
  );
};

export default DesviosTable;
